import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Activity, Loader2, Lock } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@icointel.io");
  const [password, setPassword] = useState("Admin@12345");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12">
        <div className="max-w-sm w-full">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 bg-primary flex items-center justify-center rounded-sm">
              <Activity className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-lg tracking-tight">TokenScope</div>
              <div className="overline text-muted-foreground text-[9px]">ICO Intelligence Platform</div>
            </div>
          </div>

          <div className="overline text-muted-foreground mb-2">Secure Admin Access</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">
            Token-launch intelligence.
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            Research, score, and compare the global ICO / IDO / IEO market — with source-verified evidence.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline text-muted-foreground">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" required
                className="mt-1.5 w-full h-11 px-3 text-sm bg-card border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground">Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" required
                className="mt-1.5 w-full h-11 px-3 text-sm bg-card border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring"
              />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-destructive bg-red-50 border border-red-200 rounded-sm px-3 py-2">{error}</div>}
            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 bg-primary text-primary-foreground font-semibold text-sm rounded-sm hover:bg-primary/90 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Sign in
            </button>
          </form>

          <div className="mt-6 text-[11px] text-muted-foreground border border-border/60 bg-muted/40 rounded-sm px-3 py-2">
            Single-user platform. Demo credentials are pre-filled. All analysis is research, not investment or legal advice.
          </div>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block relative bg-primary overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1669813013330-368313fa333f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJyaWdodCUyMGxpZ2h0fGVufDB8fHx8MTc4NzY1OTc5NXww&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="relative h-full flex flex-col justify-end p-14 text-primary-foreground">
          <div className="grid grid-cols-3 gap-4 mb-10">
            {[["20", "Tracked projects"], ["24", "Score components"], ["6", "Jurisdictions"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-heading font-extrabold text-4xl num">{v}</div>
                <div className="text-xs text-primary-foreground/60 mt-1">{l}</div>
              </div>
            ))}
          </div>
          <blockquote className="font-heading font-bold text-2xl leading-snug max-w-md tracking-tight">
            “What is happening across the global token-launch market — and where are the gaps?”
          </blockquote>
          <div className="text-sm text-primary-foreground/60 mt-3">Discovery · Structured data · Deep research · Scoring · Market-gap intelligence</div>
        </div>
      </div>
    </div>
  );
}
